import { type ButtonHandle, createButton } from '../primitives/button';
import './title-screen.css';

export interface TitleScreenAction {
  icon: string;
  ariaLabel: string;
  onClick: () => void;
}

export interface TitleScreenOptions {
  title: string;
  tagline?: string;
  logo?: HTMLElement | string;
  onPlay: () => void;
  onHowToPlay?: () => void;
  actions?: TitleScreenAction[];
}

export interface TitleScreenHandle {
  readonly el: HTMLElement;
  show(): void;
  hide(): void;
  destroy(): void;
}

export function createTitleScreen(opts: TitleScreenOptions): TitleScreenHandle {
  const el = document.createElement('div');
  el.className = 'fg-title';

  const logoWrap = document.createElement('div');
  logoWrap.className = 'fg-title__logo';
  if (opts.logo instanceof HTMLElement) {
    logoWrap.appendChild(opts.logo);
  } else if (typeof opts.logo === 'string') {
    const img = document.createElement('img');
    img.src = opts.logo;
    img.alt = '';
    logoWrap.appendChild(img);
  } else {
    const ph = document.createElement('div');
    ph.className = 'fg-title__logo--placeholder';
    ph.textContent = 'logo';
    logoWrap.appendChild(ph);
  }
  el.appendChild(logoWrap);

  const heading = document.createElement('h1');
  heading.className = 'fg-title__heading';
  heading.textContent = opts.title;
  el.appendChild(heading);

  if (opts.tagline) {
    const tag = document.createElement('div');
    tag.className = 'fg-title__tagline';
    tag.textContent = opts.tagline;
    el.appendChild(tag);
  }

  const ctaWrap = document.createElement('div');
  ctaWrap.className = 'fg-title__ctas';
  const playBtn = createButton({ variant: 'primary', label: 'PLAY', onClick: opts.onPlay });
  ctaWrap.appendChild(playBtn.el);

  let howBtn: ButtonHandle | null = null;
  if (opts.onHowToPlay) {
    howBtn = createButton({
      variant: 'secondary',
      label: 'HOW TO PLAY',
      onClick: opts.onHowToPlay,
    });
    ctaWrap.appendChild(howBtn.el);
  }
  el.appendChild(ctaWrap);

  const actionBtns: ButtonHandle[] = [];
  if (opts.actions && opts.actions.length > 0) {
    const actionsWrap = document.createElement('div');
    actionsWrap.className = 'fg-title__actions';
    for (const a of opts.actions) {
      const b = createButton({
        variant: 'icon',
        icon: a.icon,
        ariaLabel: a.ariaLabel,
        onClick: a.onClick,
      });
      actionsWrap.appendChild(b.el);
      actionBtns.push(b);
    }
    el.appendChild(actionsWrap);
  }

  return {
    el,
    show() {
      el.classList.add('fg-title--open');
    },
    hide() {
      el.classList.remove('fg-title--open');
    },
    destroy() {
      playBtn.destroy();
      howBtn?.destroy();
      for (const b of actionBtns) b.destroy();
      el.remove();
    },
  };
}
